import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '../../../../api/client';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { useToastStore } from '../../../../stores/toastStore';
import type { ConfidenceThresholdsResponse } from '../../../schema/api';
import { ProviderSelect } from '../../../schema/components/ProviderSelect';
import { ThresholdSliders } from '../../../schema/components/ThresholdSliders';
import type { CandidateFieldResponse } from '../api';
import { useDraft, usePublishDraft } from '../hooks';
import { useSlugAvailability } from '../useSlugAvailability';

/**
 * Mirror of the backend DocumentType.ResolveOcrModelTier inference: a table field ANYWHERE in the
 * schema (including nested inside a Group's children) means the published type will need the
 * table-capable OCR model. Must recurse into children to match the backend, otherwise the preview
 * can promise "Solo texto" while the backend resolves to "Texto + tablas".
 */
function schemaHasTableField(fields: readonly CandidateFieldResponse[]): boolean {
  return fields.some((f) => f.isTableField || (f.children ? schemaHasTableField(f.children) : false));
}

const DEFAULT_THRESHOLDS: ConfidenceThresholdsResponse = {
  fieldWarning: 0.75,
  fieldCritical: 0.55,
  documentReviewRatio: 0.5,
  documentRejectGlobalScore: 0.3,
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Step 5 — publish the draft as a full DocumentType. */
export function DiscoveryPublishStep(): JSX.Element {
  const { draftId } = useParams<{ draftId: string }>();
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);
  const { data, isLoading, isError, error } = useDraft(draftId);
  const publish = usePublishDraft();

  const [finalName, setFinalName] = useState<string>('');
  const [finalSlug, setFinalSlug] = useState<string>('');
  const [providerId, setProviderId] = useState<string>('');
  const [thresholds, setThresholds] = useState<ConfidenceThresholdsResponse>(DEFAULT_THRESHOLDS);
  const [forcePublish, setForcePublish] = useState<boolean>(false);

  const isSourced = (data?.sourceDocumentTypeSlug ?? null) !== null;

  useEffect(() => {
    if (data === undefined) {
      return;
    }
    // For re-education drafts the slug is locked to the source type's slug.
    if (data.sourceDocumentTypeSlug != null) {
      setFinalSlug(data.sourceDocumentTypeSlug);
      if (finalName === '' && data.suggestedName != null) {
        setFinalName(data.suggestedName);
      }
      return;
    }
    if (data.suggestedName !== undefined && data.suggestedName !== null && finalName === '') {
      setFinalName(data.suggestedName);
      setFinalSlug(slugify(data.suggestedName));
    }
  }, [data, finalName]);

  // Hooks must run on every render in the same order — keep useSlugAvailability ABOVE the
  // loading/error early returns below (rules-of-hooks). Passing '' for an invalid slug keeps
  // the availability check idle without skipping the hook call.
  // For sourced (re-education) drafts the slug is locked and availability checking is skipped —
  // the backend enforces that finalSlug === sourceDocumentTypeSlug and publishes as a new version.
  const slugIsValid = /^[a-z0-9_-]+$/.test(finalSlug);
  const slugAvailability = useSlugAvailability(slugIsValid && !isSourced ? finalSlug : '');

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }
  if (isError || data === undefined) {
    const message = error instanceof Error ? error.message : 'Error desconocido.';
    return (
      <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
        <strong className="font-semibold">No se pudo cargar la propuesta de tipo.</strong>
        <p className="mt-1">{message}</p>
      </div>
    );
  }

  const slugTaken = !isSourced && slugAvailability.kind === 'taken';
  const slugChecking = !isSourced && slugAvailability.kind === 'checking';
  const canSubmit =
    finalName.trim() !== '' &&
    slugIsValid &&
    providerId !== '' &&
    draftId !== undefined &&
    !slugTaken &&
    !slugChecking;

  const handleSubmit = (): void => {
    if (draftId === undefined || !canSubmit) {
      return;
    }
    publish.mutate(
      {
        draftId,
        request: {
          finalName: finalName.trim(),
          finalSlug,
          modelProviderId: providerId,
          thresholds: {
            fieldWarning: thresholds.fieldWarning,
            fieldCritical: thresholds.fieldCritical,
            documentReviewRatio: thresholds.documentReviewRatio,
            documentRejectGlobalScore: thresholds.documentRejectGlobalScore,
          },
          forcePublishDespiteWarnings: forcePublish,
        },
      },
      {
        onSuccess: (created) => {
          pushToast({
            tone: 'success',
            title: isSourced
              ? `Nueva versión publicada (v${created.version}).`
              : `Tipo "${created.slug}" publicado (versión ${created.version}).`,
          });
          navigate(`/document-types/${encodeURIComponent(created.slug)}`);
        },
        onError: (err) => {
          const message =
            err instanceof ApiError ? err.message : 'No se pudo publicar el tipo.';
          pushToast({ tone: 'error', title: 'Error al publicar', description: message });
        },
      },
    );
  };

  const hasBlockers = data.maturity.blockers.length > 0;
  const hasWarnings = data.maturity.warnings.length > 0;

  // Informational: compute which OCR model will apply once published, client-side from the
  // accepted candidate fields. The type always publishes as Auto; the backend infers the same
  // logic. Showing it here confirms to the operator what they will get without adding a new
  // field to the publish request.
  const acceptedFields = data.candidateFields.filter(
    (f) => f.status === 'Accepted' || f.status === 'Modified',
  );
  const inferredOcrLabel = schemaHasTableField(acceptedFields)
    ? 'Texto + tablas (prebuilt-layout)'
    : 'Solo texto (prebuilt-read)';

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Publicar propuesta de tipo</h1>
        <p className="mt-1 text-sm text-slate-500">
          {isSourced
            ? `Reeducación de "${data.sourceDocumentTypeSlug ?? ''}". El slug está bloqueado y se publicará como nueva versión.`
            : 'Define el nombre y el slug definitivos. El tipo se publica como versión 1.0.0.'}
        </p>
      </header>

      <section className="card p-5">
        <h2 className="text-base font-semibold text-slate-900">Identidad</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="label" htmlFor="publish-name">
              Nombre final
            </label>
            <input
              id="publish-name"
              className="input"
              value={finalName}
              onChange={(event) => {
                setFinalName(event.target.value);
                if (finalSlug === '' || finalSlug === slugify(finalName)) {
                  setFinalSlug(slugify(event.target.value));
                }
              }}
              disabled={publish.isPending}
            />
          </div>
          <div>
            <label className="label" htmlFor="publish-slug">
              Slug
            </label>
            <input
              id="publish-slug"
              className="input font-mono text-sm"
              value={finalSlug}
              onChange={(event) => setFinalSlug(event.target.value)}
              disabled={publish.isPending || isSourced}
            />
            {isSourced ? (
              <p className="mt-1 text-xs text-indigo-700">
                Se publicará como nueva versión de este tipo (versionado automático, con rollback disponible).
              </p>
            ) : null}
            {!isSourced && !slugIsValid && finalSlug !== '' ? (
              <p className="mt-1 text-xs text-rose-700">
                Solo se permiten minúsculas, dígitos, guiones y guiones bajos.
              </p>
            ) : null}
            {!isSourced && slugIsValid && slugChecking ? (
              <p className="mt-1 text-xs text-slate-500">Comprobando disponibilidad…</p>
            ) : null}
            {!isSourced && slugIsValid && slugTaken ? (
              <div
                className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                data-testid="slug-taken-banner"
              >
                <p>
                  El slug <strong className="font-mono">{finalSlug}</strong> ya está en uso.
                  {slugAvailability.kind === 'taken' && slugAvailability.suggestion !== null ? (
                    <> Sugerencia: <strong className="font-mono">{slugAvailability.suggestion}</strong>.</>
                  ) : (
                    <> Cambia el slug para poder publicar.</>
                  )}
                </p>
                {slugAvailability.kind === 'taken' && slugAvailability.suggestion !== null ? (
                  <button
                    type="button"
                    className="btn-secondary mt-2 px-2 py-1 text-xs"
                    onClick={() => {
                      if (slugAvailability.kind === 'taken' && slugAvailability.suggestion !== null) {
                        setFinalSlug(slugAvailability.suggestion);
                      }
                    }}
                    disabled={publish.isPending}
                  >
                    Usar {slugAvailability.suggestion}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="md:col-span-2">
            <label className="label">Modelo IA</label>
            <ProviderSelect
              value={providerId}
              onChange={setProviderId}
              disabled={publish.isPending}
            />
          </div>
          <div className="md:col-span-2">
            <p className="text-xs text-slate-500" data-testid="ocr-model-inferred-label">
              OCR que se aplicará:{' '}
              <span className="font-medium text-slate-700">{inferredOcrLabel}</span>{' '}
              (automático según el esquema; ajustable luego en el editor).
            </p>
          </div>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base font-semibold text-slate-900">Umbrales de confianza</h2>
        <p className="mt-1 text-sm text-slate-500">
          Puedes ajustarlos después desde el editor del tipo publicado.
        </p>
        <div className="mt-4">
          <ThresholdSliders
            value={thresholds}
            onChange={setThresholds}
            disabled={publish.isPending}
          />
        </div>
      </section>

      {hasWarnings || hasBlockers ? (
        <section className="card border-amber-200 bg-amber-50 p-5">
          <h2 className="text-base font-semibold text-amber-900">
            Hay {hasBlockers ? 'bloqueos' : 'advertencias'} pendientes
          </h2>
          <p className="mt-1 text-sm text-amber-900">
            {hasBlockers
              ? 'La publicación está bloqueada. Vuelve a la revisión o marca la casilla de forzar publicación si el bloqueo es asumible.'
              : 'Puedes publicar tal cual; activa la casilla para registrar que conoces las advertencias.'}
          </p>
          <label className="mt-3 flex items-center gap-2 text-sm text-amber-900">
            <input
              type="checkbox"
              checked={forcePublish}
              onChange={(event) => setForcePublish(event.target.checked)}
              disabled={publish.isPending}
            />
            Publicar a pesar de advertencias y bloqueos
          </label>
        </section>
      ) : null}

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => {
            if (draftId !== undefined) {
              navigate(
                `/document-types/discover/${encodeURIComponent(draftId)}/maturity`,
              );
            }
          }}
          disabled={publish.isPending}
        >
          ← Atrás
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!canSubmit || publish.isPending}
        >
          {publish.isPending ? 'Publicando…' : 'Publicar tipo de documento'}
        </button>
      </div>
    </div>
  );
}
